import React, { useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import { MessageCircle, Phone, Mail, MapPin, Send, Download, Smartphone } from "lucide-react";
import { SITE, buildWhatsAppLink } from "../mock/mock";
import { useToast } from "../hooks/use-toast";

const Contact = () => {
  const { toast } = useToast();
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);

  const whatsappUrl = buildWhatsAppLink("Bonjour GwadaPlaisance, je souhaite avoir des informations sur vos excursions.");

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.name || !form.message) {
      toast({ title: "Champs manquants", description: "Merci de renseigner votre nom et un message." });
      return;
    }
    setSending(true);
    // Compose WhatsApp text and open WhatsApp
    const text =
      `Bonjour, je suis ${form.name}.\n` +
      (form.email ? `Email : ${form.email}\n` : "") +
      (form.phone ? `Tél : ${form.phone}\n` : "") +
      `\n${form.message}`;
    window.open(buildWhatsAppLink(text), "_blank");
    setTimeout(() => {
      setSending(false);
      toast({
        title: "Message prêt sur WhatsApp",
        description: "WhatsApp s'est ouvert avec votre message. Envoyez-le pour finaliser votre demande.",
      });
      setForm({ name: "", email: "", phone: "", message: "" });
    }, 600);
  };

  const downloadQR = () => {
    const svg = document.getElementById("gwada-qr");
    if (!svg) return;
    const serializer = new XMLSerializer();
    const svgStr = serializer.serializeToString(svg);
    const blob = new Blob([svgStr], { type: "image/svg+xml;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "gwadaplaisance-whatsapp-qr.svg";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast({ title: "QR Code téléchargé", description: "Partagez-le pour permettre une réservation rapide." });
  };

  return (
    <section id="contact" className="py-16 lg:py-24 bg-gradient-to-b from-slate-50 to-white">
      <div className="max-w-7xl mx-auto px-4 lg:px-6">
        <div className="text-center mb-12">
          <span className="inline-block bg-[#25D366]/10 text-[#25D366] text-xs font-black tracking-widest px-3 py-1 rounded-full">
            CONTACT
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-black text-[#0B2545]">
            Réservez votre expérience en mer
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
            Scannez le QR code ou envoyez-nous un message WhatsApp — nous répondons généralement en moins d&apos;une heure.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Left: QR + coords */}
          <div className="space-y-6">
            <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
              <div className="flex flex-col md:flex-row items-center gap-8">
                <div className="relative shrink-0">
                  <div className="bg-white p-4 rounded-2xl border-4 border-[#25D366] shadow-lg">
                    <QRCodeSVG
                      id="gwada-qr"
                      value={whatsappUrl}
                      size={200}
                      level="H"
                      includeMargin={false}
                      fgColor="#0B2545"
                      imageSettings={{
                        src: "data:image/svg+xml;utf8," +
                          encodeURIComponent(
                            '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 40 40"><rect width="40" height="40" rx="8" fill="#25D366"/><path fill="white" d="M20 8a12 12 0 0 0-10.3 18.1L8 32l6-1.6A12 12 0 1 0 20 8Zm7 17c-.3.8-1.7 1.6-2.4 1.6-.6 0-1.4 0-3.6-1a12 12 0 0 1-5.6-5c-.4-.7-1-1.9-1-3 0-1.3.7-2 1-2.3.2-.3.5-.3.8-.3h.5c.2 0 .5-.1.8.6l1 2.3c.1.2.1.4 0 .5l-.4.5-.4.4c-.1.1-.3.3-.1.6a9 9 0 0 0 1.7 2.2c1 .9 2 1.2 2.2 1.3.2.1.4.1.5-.1l.9-1.1c.2-.2.4-.2.6-.1l2.1 1c.3.1.5.2.5.3.1.2.1.9-.1 1.5Z"/></svg>'
                          ),
                        height: 40,
                        width: 40,
                        excavate: true,
                      }}
                    />
                  </div>
                </div>
                <div className="flex-1 text-center md:text-left">
                  <div className="inline-flex items-center gap-2 bg-[#25D366]/10 text-[#25D366] font-bold px-3 py-1 rounded-full text-xs tracking-widest">
                    <Smartphone className="h-3.5 w-3.5" /> SCAN & CHAT
                  </div>
                  <h3 className="mt-3 text-2xl font-black text-[#0B2545]">
                    Scannez pour discuter sur WhatsApp
                  </h3>
                  <p className="mt-2 text-slate-600 text-sm">
                    Pointez l&apos;appareil photo de votre smartphone vers le QR code, ou utilisez le bouton ci-dessous.
                  </p>
                  <div className="mt-4 flex flex-col sm:flex-row gap-3">
                    <a
                      href={whatsappUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] text-white font-bold px-5 py-3 rounded-xl transition"
                    >
                      <MessageCircle className="h-5 w-5" fill="white" strokeWidth={0} />
                      OUVRIR WHATSAPP
                    </a>
                    <button
                      onClick={downloadQR}
                      className="inline-flex items-center justify-center gap-2 bg-white border-2 border-[#0B2545] text-[#0B2545] hover:bg-[#0B2545] hover:text-white font-bold px-5 py-3 rounded-xl transition"
                    >
                      <Download className="h-5 w-5" />
                      TÉLÉCHARGER QR
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid sm:grid-cols-2 gap-4">
              <a
                href={`tel:+590${SITE.phoneRaw.substring(1)}`}
                className="bg-white rounded-2xl p-5 shadow-md border border-slate-100 hover:shadow-lg transition group"
              >
                <div className="h-11 w-11 rounded-full bg-[#25D366]/10 flex items-center justify-center group-hover:bg-[#25D366]/20 transition">
                  <Phone className="h-5 w-5 text-[#25D366]" />
                </div>
                <div className="mt-3 text-xs text-slate-500 font-bold tracking-widest">TÉLÉPHONE</div>
                <div className="font-black text-[#0B2545] text-lg tracking-wide">{SITE.phone}</div>
              </a>
              <a
                href={`mailto:${SITE.email}`}
                className="bg-white rounded-2xl p-5 shadow-md border border-slate-100 hover:shadow-lg transition group"
              >
                <div className="h-11 w-11 rounded-full bg-[#FFC93C]/20 flex items-center justify-center group-hover:bg-[#FFC93C]/30 transition">
                  <Mail className="h-5 w-5 text-[#0B2545]" />
                </div>
                <div className="mt-3 text-xs text-slate-500 font-bold tracking-widest">EMAIL</div>
                <div className="font-black text-[#0B2545] text-sm break-all">{SITE.email}</div>
              </a>
              <div className="sm:col-span-2 bg-white rounded-2xl p-5 shadow-md border border-slate-100">
                <div className="flex items-start gap-3">
                  <div className="h-11 w-11 rounded-full bg-[#0B2545]/10 flex items-center justify-center shrink-0">
                    <MapPin className="h-5 w-5 text-[#0B2545]" />
                  </div>
                  <div>
                    <div className="text-xs text-slate-500 font-bold tracking-widest">POINT DE RENDEZ-VOUS</div>
                    <div className="font-black text-[#0B2545]">Marina du Gosier — Guadeloupe</div>
                    <div className="text-slate-600 text-sm">Départ quotidien selon réservation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div className="bg-white rounded-2xl shadow-xl border border-slate-100 p-8">
            <h3 className="font-black text-[#0B2545] text-xl">Envoyez-nous un message</h3>
            <p className="text-sm text-slate-500 mt-1">Votre message sera transmis via WhatsApp</p>
            <form onSubmit={handleSubmit} className="mt-5 space-y-4">
              <div>
                <label className="block text-xs font-bold text-[#0B2545] tracking-widest mb-1">NOM *</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#25D366] focus:outline-none transition text-[#0B2545]"
                  placeholder="Votre nom"
                  required
                />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-bold text-[#0B2545] tracking-widest mb-1">EMAIL</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#25D366] focus:outline-none transition text-[#0B2545]"
                    placeholder="votre@email.com"
                  />
                </div>
                <div>
                  <label className="block text-xs font-bold text-[#0B2545] tracking-widest mb-1">TÉLÉPHONE</label>
                  <input
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#25D366] focus:outline-none transition text-[#0B2545]"
                    placeholder="06 90 XX XX XX"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-bold text-[#0B2545] tracking-widest mb-1">MESSAGE *</label>
                <textarea
                  rows={5}
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl border-2 border-slate-200 focus:border-[#25D366] focus:outline-none transition text-[#0B2545] resize-none"
                  placeholder="Excursion souhaitée, date, nombre de personnes..."
                  required
                />
              </div>
              <button
                type="submit"
                disabled={sending}
                className="w-full flex items-center justify-center gap-2 bg-[#25D366] hover:bg-[#1EBE5D] disabled:opacity-70 text-white font-bold px-5 py-4 rounded-xl transition shadow-lg"
              >
                <Send className="h-5 w-5" />
                {sending ? "Ouverture WhatsApp..." : "ENVOYER VIA WHATSAPP"}
              </button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
